"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useMemo, useState } from "react"

interface Suburb {
  id: string
  slug: string
  name: string
  city: string
  activeListings: number
  /** Admin cover photo, else the newest live listing photo there, else null. */
  imageUrl: string | null
  medianListing: number | null
  growth90d: number | null
  growth12m: number | null
}

const CITY_ORDER = ["Harare", "Bulawayo", "Mutare", "Gweru", "Victoria Falls", "Masvingo", "Kwekwe", "Chinhoyi", "Marondera", "Bindura", "Kariba", "Beitbridge", "Chegutu", "Zvishavane", "Hwange", "Kadoma", "Karoi"]

/**
 * Photo strip at the top of a directory card. The cards used to be text only.
 * With no photo yet, a branded placeholder keeps every card the same height so
 * the grid does not jump around as photos are added.
 */
function CardThumb({ src, label }: { src: string | null; label: string }) {
  return (
    <div style={{ position: "relative", height: 140, background: "linear-gradient(135deg,var(--navy),var(--navy-soft))", overflow: "hidden" }}>
      {src ? (
        <Image src={src} alt={label} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />
      ) : (
        <div aria-hidden style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", backgroundImage: "radial-gradient(circle, rgba(253,251,247,0.08) 1.5px, transparent 1.5px)", backgroundSize: "18px 18px" }}>
          <span style={{ fontFamily: "Fraunces, serif", fontSize: 44, fontWeight: 700, color: "rgba(200,150,62,0.55)" }}>{label.trim()[0]?.toUpperCase() ?? ""}</span>
        </div>
      )}
    </div>
  )
}

export default function SuburbsPage() {
  const [suburbs, setSuburbs] = useState<Suburb[]>([])
  const [loading, setLoading] = useState(true)
  const [city, setCity] = useState("All")
  const [search, setSearch] = useState("")
  const [email, setEmail] = useState("")
  const [subscribed, setSubscribed] = useState(false)

  useEffect(() => {
    fetch("/api/suburbs")
      .then(r => r.json())
      .then(d => { setSuburbs(d.data ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const cities = useMemo(() => {
    const present = new Set(suburbs.map(s => s.city))
    const known = CITY_ORDER.filter(c => present.has(c))
    const extra = [...present].filter(c => !CITY_ORDER.includes(c)).sort()
    return [...known, ...extra]
  }, [suburbs])

  const citySummaries = useMemo(() => cities.map(c => {
    const cs = suburbs.filter(s => s.city === c)
    const totalListings = cs.reduce((sum, s) => sum + s.activeListings, 0)
    const imageUrl = [...cs].sort((a, b) => b.activeListings - a.activeListings).find(s => s.imageUrl)?.imageUrl ?? null
    return { city: c, count: cs.length, totalListings, imageUrl }
  }), [suburbs, cities])

  const filtered = useMemo(() => {
    let list = city === "All" ? suburbs : suburbs.filter(s => s.city === city)
    if (search.trim()) list = list.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.city.toLowerCase().includes(search.toLowerCase()))
    return [...list].sort((a, b) => b.activeListings - a.activeListings)
  }, [suburbs, city, search])

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>

      {/* HERO */}
      <div style={{ background: "var(--navy)", padding: "64px 24px 0" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <span className="eyebrow hero-animate-1" style={{ background: "rgba(200,150,62,0.15)", borderColor: "rgba(200,150,62,0.3)", color: "var(--gold-soft)" }}>Suburb directory</span>
          <h1 className="hero-animate-2" style={{ fontFamily: "Fraunces, serif", fontSize: 48, fontWeight: 700, color: "var(--cream)", margin: "20px 0 12px", lineHeight: 1.1 }}>
            Zimbabwe property <em style={{ color: "var(--gold)" }}>by suburb</em>.
          </h1>
          <p className="hero-animate-3" style={{ color: "rgba(253,251,247,0.75)", fontSize: 16, maxWidth: 520, marginBottom: 40 }}>
            Browse active listings across {loading ? "-" : cities.length} cities and their suburbs.
          </p>

          {/* CITY TABS */}
          <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 0, scrollbarWidth: "none" }}>
            {["All", ...cities].map(c => (
              <button key={c} onClick={() => { setCity(c); setSearch("") }}
                style={{ padding: "10px 18px", borderRadius: "10px 10px 0 0", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0, background: city === c ? "var(--cream)" : "rgba(253,251,247,0.08)", color: city === c ? "var(--navy)" : "rgba(253,251,247,0.65)", transition: "all 0.18s" }}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="section">

        {/* INFO BANNER */}
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "rgba(27,45,84,0.04)", border: "1px solid var(--line)", borderRadius: 12, padding: "14px 18px", marginBottom: 28, fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
          <span>Listing counts update in real time. Price-per-m² and growth figures populate from verified agent-reported sales data as it is recorded.</span>
        </div>

        {/* SEARCH */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ position: "relative", maxWidth: 480 }}>
            <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx={11} cy={11} r={8}/><path d="m21 21-4.35-4.35"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={city === "All" ? "Search any suburb or city…" : `Search in ${city}…`}
              style={{ width: "100%", padding: "10px 14px 10px 36px", border: "1px solid var(--line)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", color: "var(--navy)", outline: "none", boxSizing: "border-box" }} />
          </div>
        </div>

        {loading ? (
          <div className="mob-grid-3" style={{ gap: 16, marginBottom: 48 }}>
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, height: 250, opacity: 0.4 + i * 0.03 }} />
            ))}
          </div>
        ) : (
          <>
            {/* CITY OVERVIEW CARDS, only when no search/filter */}
            {city === "All" && !search && (
              <div style={{ marginBottom: 48 }}>
                <h2 className="section-title reveal" style={{ marginBottom: 6 }}>Browse by city</h2>
                <p className="section-sub reveal" style={{ marginBottom: 24 }}>Select a city to see its suburbs.</p>
                <div className="mob-grid-3" style={{ gap: 16, marginBottom: 0 }}>
                  {citySummaries.map((cs, i) => (
                    <button key={cs.city} onClick={() => setCity(cs.city)} className="reveal-scale"
                      style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 0, overflow: "hidden", textAlign: "left", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", transitionDelay: `${Math.min(i, 5) * 60}ms` }}>
                      <CardThumb src={cs.imageUrl} label={cs.city} />
                      <div style={{ padding: 20 }}>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: "var(--navy)", marginBottom: 14 }}>{cs.city}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Suburbs</div>
                          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>{cs.count}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Active listings</div>
                          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: cs.totalListings > 0 ? "var(--green)" : "var(--navy)" }}>{cs.totalListings || "-"}</div>
                        </div>
                      </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CITY HEADING when filtered */}
            {(city !== "All" || search) && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: "var(--navy)", marginBottom: 2 }}>
                    {search ? `Results for "${search}"` : city}
                  </h2>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>{filtered.length} suburb{filtered.length !== 1 ? "s" : ""}</div>
                </div>
                {city !== "All" && (
                  <button onClick={() => setCity("All")} style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", background: "none", border: "1px solid var(--line)", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontFamily: "inherit" }}>← All cities</button>
                )}
              </div>
            )}

            {/* SUBURB GRID */}
            <div className="mob-grid-3" style={{ gap: 16, marginBottom: 48 }}>
              {filtered.map((suburb, i) => (
                <Link key={suburb.slug} href={"/suburbs/" + suburb.slug} className="reveal-scale"
                  style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 16, padding: 0, overflow: "hidden", display: "block", textDecoration: "none", transitionDelay: `${Math.min(i, 8) * 50}ms` }}>
                  <CardThumb src={suburb.imageUrl} label={suburb.name} />
                  <div style={{ padding: 20 }}>

                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "var(--navy)", marginBottom: 4, lineHeight: 1.2 }}>{suburb.name}</div>
                  {city === "All" && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", marginBottom: 14 }}>{suburb.city}</div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: city === "All" ? 0 : 14 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>Active listings</div>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: suburb.activeListings > 0 ? "var(--navy)" : "var(--line)" }}>
                        {suburb.activeListings || "-"}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>12m growth</div>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: suburb.growth12m != null ? (suburb.growth12m > 4 ? "var(--green)" : suburb.growth12m > 0 ? "var(--gold-deep)" : "var(--red)") : "var(--line)" }}>
                        {suburb.growth12m != null ? `${suburb.growth12m > 0 ? "+" : ""}${suburb.growth12m}%` : "-"}
                      </div>
                    </div>
                  </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && !loading && (
              <div style={{ textAlign: "center", padding: "64px 24px", color: "var(--muted)" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, color: "var(--navy)", marginBottom: 8 }}>No suburbs found</div>
                <div style={{ fontSize: 14 }}>Try a different search or select another city.</div>
              </div>
            )}
          </>
        )}

        {/* NEWSLETTER CTA */}
        <div className="reveal-scale" style={{ background: "linear-gradient(135deg,var(--navy),#2a3f6e)", borderRadius: 20, padding: "48px 40px", textAlign: "center" }}>
          <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 600, color: "var(--cream)", marginBottom: 10 }}>
            Free monthly market report
          </h3>
          <p style={{ color: "rgba(253,251,247,0.72)", fontSize: 14, maxWidth: 460, margin: "0 auto 24px", lineHeight: 1.6 }}>
            Suburb price trends, fastest-growing areas, fraud alerts, and diaspora investment insights, every month, free.
          </p>
          {subscribed ? (
            <div style={{ color: "var(--gold)", fontWeight: 600, fontSize: 15 }}>✓ You&apos;re on the list</div>
          ) : (
            <div style={{ display: "flex", gap: 10, maxWidth: 440, margin: "0 auto", flexWrap: "wrap" }}>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                style={{ flex: 1, minWidth: 200, padding: "13px 16px", border: "1px solid rgba(253,251,247,0.2)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", background: "rgba(253,251,247,0.08)", color: "var(--cream)", outline: "none" }} />
              <button
                onClick={() => { if (email) fetch("/api/newsletter", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }).then(r => r.json()).then(d => { if (d.success) setSubscribed(true) }).catch(() => {}) }}
                style={{ padding: "13px 24px", background: "var(--gold)", color: "var(--navy)", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                Subscribe free
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
